import type { IoTAlert, IoTNode, NodeLatest } from './iotApi';
import type { Equipment, HydraulicLink, IrrigationBlock, Sensor } from '../types/digitalTwin';
import {
  getAirHumidity,
  getAirTemperature,
  getNodeBattery,
  getNodeFlow,
  getNodeValveState,
  getPressure,
  getPumpState,
  getSoilEc,
  getSoilMoisture,
  getSoilTemperature,
} from './liveSensorMetrics';

export type TelemetrySource = 'real' | 'demo' | 'none';

export type TelemetryValue = {
  key: TelemetryMetricKey;
  label: string;
  labelKey?: string;
  value: string;
};

/** Known telemetry metric keys — used for i18n label mapping */
export type TelemetryMetricKey = string;

/** Maps internal metric keys to i18n telemetryLabels keys */
const METRIC_LABEL_KEY_MAP: Record<string, string> = {
  valve_state: 'valve',
  pump_state: 'pump',
  flow: 'flow',
  pressure: 'pressure',
  soil_moisture: 'soilMoisture',
  soil_ec: 'soilEc',
  soil_temperature: 'soilTemperature',
  air_temperature: 'airTemperature',
  air_humidity: 'airHumidity',
  leaf_moisture: 'leafMoisture',
  current: 'current',
  water_level: 'waterLevel',
  ultrasonic_range: 'ultrasonicRange',
  battery: 'battery',
  signal: 'signal',
  tank_level: 'tankLevel',
  water_meter: 'waterMeter',
  fertilizer: 'fertilizer',
  ph: 'pH',
  ec: 'EC',
};

export type DemoTelemetryHistory = {
  lastIrrigationEvent: string;
  averagePressureBar: number;
  waterDeliveredTodayM3: number;
  soilMoistureTrend: 'rising' | 'stable' | 'falling';
  lastUpdateTime: string;
};

export type EquipmentTelemetry = {
  source: TelemetrySource;
  online: boolean;
  active: boolean | null;
  statusKey: string;
  sourceNodeId?: string;
  sourceNodeName?: string;
  lastUpdated?: string;
  valveState?: 'open' | 'closed';
  pumpState?: 'on' | 'off';
  pressureState?: 'nominal' | 'low' | 'high' | 'inactive';
  waterDeliveryEstimate?: {
    key: string;
    params: Record<string, unknown>;
  };
  alert?: {
    severity: string;
    message: string;
  };
  values: TelemetryValue[];
  fallbackKey: string;
  demoAssumptions?: DemoAssumption[];
  history?: DemoTelemetryHistory;
};

export type DemoAssumption = {
  key: string;
  params?: Record<string, unknown>;
};

type DemoContext = {
  enabled: boolean;
  now?: Date;
};

const normalize = (value?: string | null): string =>
  (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const stringField = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

const numericField = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const configNumber = (value: Record<string, unknown>, keys: string[]): number | undefined => {
  for (const key of keys) {
    const candidate = numericField(value[key]);
    if (candidate != null) return candidate;
  }
  return undefined;
};

const equipmentMatchTokens = (equipment: Equipment): string[] => {
  const config = equipment.configuration || {};
  const communication = equipment.communication_parameters || {};
  return [
    equipment.id,
    equipment.name,
    equipment.serial_number,
    stringField(config.dev_eui),
    stringField(config.ttn_device_id),
    stringField(config.node_id),
    stringField(communication.dev_eui),
    stringField(communication.ttn_device_id),
    stringField(communication.node_id),
  ].map(normalize).filter(Boolean);
};

export const findTelemetryNode = (equipment: Equipment, nodes: IoTNode[]): IoTNode | undefined => {
  const tokens = equipmentMatchTokens(equipment);
  if (!tokens.length) return undefined;
  return nodes.find((node) => {
    const nodeTokens = [
      node.id,
      node.name,
      node.dev_eui,
      node.ttn_device_id,
    ].map(normalize).filter(Boolean);
    return nodeTokens.some((token) => tokens.includes(token));
  });
};

const getMeasure = (measures: NodeLatest['measures'] | undefined, keys: string[]): number | undefined => {
  const values = (measures || {}) as Record<string, unknown>;
  for (const key of keys) {
    const value = values[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
};

const sensorValue = (sensor: Sensor, measures: NodeLatest['measures'] | undefined): TelemetryValue | null => {
  const value = getMeasure(measures, [
    sensor.metric,
    sensor.type,
    `${sensor.metric}_value`,
  ]);
  if (value == null) return null;
  return {
    key: sensor.id,
    label: sensor.name,
    value: `${value} ${sensor.unit}`.trim(),
    // User-configured sensor name — treated as data, not translation key
  };
};

const alertFor = (equipment: Equipment, node: IoTNode | undefined, alerts: IoTAlert[]) =>
  alerts.find((alert) => {
    if (alert.status && alert.status !== 'open') return false;
    const source = normalize(alert.node_name);
    if (!source) return false;
    return source === normalize(node?.name) || source === normalize(equipment.name);
  });

export const isDigitalTwinDemoTelemetryAllowed = (): boolean =>
  process.env.REACT_APP_DIGITAL_TWIN_DEMO_TELEMETRY === 'true';

const stableHash = (input: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0);
};

const wave = (seed: string, now: Date, min: number, max: number): number => {
  const hour = now.getUTCHours() + now.getUTCMinutes() / 60;
  const phase = (stableHash(seed) % 628) / 100;
  const ratio = (Math.sin((hour / 24) * Math.PI * 2 + phase) + 1) / 2;
  return min + (max - min) * ratio;
};

const round = (value: number, digits = 1): number => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const isValveType = (type: string): boolean => ['valve', 'electrovalve', 'valve_node'].includes(type);
const isPumpType = (type: string): boolean => ['pump', 'electric_pump', 'diesel_pump', 'solar_pump', 'motor_node'].includes(type);
const isSourceType = (type: string): boolean => ['water_source', 'well', 'borehole', 'basin', 'reservoir'].includes(type);
const isPressureType = (type: string): boolean => ['pressure_sensor'].includes(type);
const isFlowType = (type: string): boolean => ['flow_meter'].includes(type);
const isSoilOrClimateType = (type: string): boolean => ['sensor_node', 'soil_air_ecth'].includes(type);

const blockForEquipment = (equipment: Equipment, blocks: IrrigationBlock[]): IrrigationBlock | undefined =>
  blocks.find((block) => block.id === equipment.block_id)
  || blocks.find((block) => normalize(block.name) && normalize(equipment.name).includes(normalize(block.name)))
  || blocks[0];

export const theoreticalFlowM3H = (block?: IrrigationBlock): number => {
  if (!block) return 4.8;
  if (block.theoretical_flow_m3_h != null) return block.theoretical_flow_m3_h;
  if (block.theoretical_volume_m3_h != null) return block.theoretical_volume_m3_h;
  if (block.line_count && block.emitters_per_line && block.emitter_flow_l_h) {
    return (block.line_count * block.emitters_per_line * block.emitter_flow_l_h) / 1000;
  }
  return 4.8;
};

const isOliveBlock = (block?: IrrigationBlock): boolean => {
  const text = [
    block?.name,
    block?.crop_id,
    block?.variety_id,
    block?.irrigation_type,
  ].map((item) => item || '').join(' ').toLowerCase();
  return text.includes('olive') || text.includes('olea') || text.includes('koroneiki');
};

const valveOpenForBlock = (block: IrrigationBlock | undefined, now: Date): boolean => {
  const month = now.getUTCMonth() + 1;
  const summer = month >= 6 && month <= 9;
  const startHour = summer ? 6 + (stableHash(block?.id || 'default-block') % 2) : 7;
  const durationHours = summer ? 3 : 1;
  const hour = now.getUTCHours() + now.getUTCMinutes() / 60;
  return hour >= startHour && hour < startHour + durationHours;
};

const lastIrrigationEvent = (block: IrrigationBlock | undefined, now: Date): string => {
  const event = new Date(now);
  const startHour = (now.getUTCMonth() + 1) >= 6 && (now.getUTCMonth() + 1) <= 9
    ? 6 + (stableHash(block?.id || 'default-block') % 2)
    : 7;
  event.setUTCHours(startHour, 0, 0, 0);
  if (event > now) event.setUTCDate(event.getUTCDate() - 1);
  return event.toISOString();
};

const connectedValveBlocks = (
  equipment: Equipment,
  allEquipment: Equipment[],
  blocks: IrrigationBlock[],
): IrrigationBlock[] => {
  if (isValveType(equipment.type)) return [blockForEquipment(equipment, blocks)].filter(Boolean) as IrrigationBlock[];
  const valveBlocks = allEquipment
    .filter((item) => isValveType(item.type))
    .map((item) => blockForEquipment(item, blocks))
    .filter(Boolean) as IrrigationBlock[];
  return valveBlocks.length ? valveBlocks : blocks;
};

const waterDeliveryEstimate = (flowM3H: number, active: boolean): EquipmentTelemetry['waterDeliveryEstimate'] =>
  active
    ? { key: 'waterDeliveryEstimate.active', params: { flow: round(flowM3H, 2) } }
    : { key: 'waterDeliveryEstimate.inactive', params: {} };

const pressureStateFor = (pressure: number, nominal: number, active: boolean): EquipmentTelemetry['pressureState'] => {
  if (!active) return 'inactive';
  if (pressure < nominal * 0.82) return 'low';
  if (pressure > nominal * 1.18) return 'high';
  return 'nominal';
};

const buildDemoTelemetry = ({
  item,
  allEquipment,
  sensors,
  blocks,
  now,
}: {
  item: Equipment;
  allEquipment: Equipment[];
  sensors: Sensor[];
  blocks: IrrigationBlock[];
  links: HydraulicLink[];
  now: Date;
}): EquipmentTelemetry => {
  const block = blockForEquipment(item, blocks);
  const valveBlocks = connectedValveBlocks(item, allEquipment, blocks);
  const anyValveActive = valveBlocks.some((candidate) => valveOpenForBlock(candidate, now));
  const active = isValveType(item.type) ? valveOpenForBlock(block, now) : (isPumpType(item.type) || isSourceType(item.type) || isFlowType(item.type) || isPressureType(item.type)) ? anyValveActive : null;
  const nominalPressure = block?.nominal_pressure_bar ?? configNumber(item.hydraulic_parameters, ['nominal_pressure_bar', 'pressure_bar']) ?? 2.4;
  const flowM3H = theoreticalFlowM3H(block);
  const flowLpm = active ? (flowM3H * 1000) / 60 : 0;
  const pressure = active ? nominalPressure + wave(`${item.id}:pressure`, now, -0.12, 0.12) : wave(`${item.id}:pressure-idle`, now, 0.25, 0.55);
  const olive = isOliveBlock(block);
  const summer = now.getUTCMonth() + 1 >= 6 && now.getUTCMonth() + 1 <= 9;
  const soilMoistureBase = olive ? (summer ? 31 : 36) : (summer ? 38 : 44);
  const soilMoisture = soilMoistureBase + (active ? 3.5 : -1.5) + wave(`${item.id}:soil`, now, -2.2, 2.2);
  const soilEc = (olive ? 1280 : 980) + wave(`${item.id}:ec`, now, -95, 130);
  const airTemp = (summer ? 30 : 20) + wave(`${item.id}:air`, now, -4.5, 5.5);
  const soilTemp = airTemp - 4 + wave(`${item.id}:soil-temp`, now, -1, 1);
  const humidity = (summer ? 52 : 67) + wave(`${item.id}:humidity`, now, -8, 8);
  const current = active ? (configNumber(item.electrical_parameters, ['nominal_current_a', 'current_a']) ?? 5.5) + wave(`${item.id}:current`, now, -0.5, 0.5) : 0;
  const waterLevel = configNumber(item.configuration, ['level_m', 'water_level_m']) ?? wave(`${item.id}:level`, now, 2.2, 4.6);
  const ultrasonicRange = round(Math.max(150, 5000 - waterLevel * 1000), 0);
  const linkedSensorCount = sensors.filter((sensor) => sensor.equipment_id === item.id || sensor.block_id === block?.id).length;
  const values: TelemetryValue[] = [];

  if (isValveType(item.type)) values.push({ key: 'valve_state', label: 'Valve', labelKey: 'valve', value: active ? 'open' : 'closed' });
  if (isPumpType(item.type) || isSourceType(item.type)) values.push({ key: 'pump_state', label: 'Pump', labelKey: 'pump', value: active ? 'on' : 'off' });
  if (isValveType(item.type) || isFlowType(item.type) || isPumpType(item.type)) values.push({ key: 'flow', label: 'Flow', labelKey: 'flow', value: `${round(flowLpm, 1)} L/min` });
  if (isPressureType(item.type) || isPumpType(item.type) || isValveType(item.type) || item.type === 'collector') values.push({ key: 'pressure', label: 'Pressure', labelKey: 'pressure', value: `${round(pressure, 2)} bar` });
  if (isSoilOrClimateType(item.type) || linkedSensorCount > 0 || item.type.includes('sensor')) {
    values.push(
      { key: 'soil_moisture', label: 'Soil moisture', labelKey: 'soilMoisture', value: `${round(soilMoisture, 1)} %` },
      { key: 'soil_ec', label: 'Soil EC', labelKey: 'soilEc', value: `${round(soilEc, 0)} µS/cm` },
      { key: 'soil_temperature', label: 'Soil temp', labelKey: 'soilTemperature', value: `${round(soilTemp, 1)} °C` },
      { key: 'air_temperature', label: 'Air temp', labelKey: 'airTemperature', value: `${round(airTemp, 1)} °C` },
      { key: 'air_humidity', label: 'Air humidity', labelKey: 'airHumidity', value: `${round(humidity, 0)} %` },
    );
  }
  if (item.type.includes('leaf')) values.push({ key: 'leaf_moisture', label: 'Leaf moisture', labelKey: 'leafMoisture', value: `${round(humidity + 12, 0)} %` });
  if (isPumpType(item.type)) values.push({ key: 'current', label: 'Current', labelKey: 'current', value: `${round(current, 1)} A` });
  if (isSourceType(item.type)) values.push(
    { key: 'water_level', label: 'Water level', labelKey: 'waterLevel', value: `${round(waterLevel, 2)} m` },
    { key: 'ultrasonic_range', label: 'Ultrasonic range', labelKey: 'ultrasonicRange', value: `${ultrasonicRange} mm` },
  );
  values.push({ key: 'battery', label: 'Battery', labelKey: 'battery', value: `${round(3.58 + wave(`${item.id}:battery`, now, 0, 0.22), 2)} V` });

  const waterToday = active ? flowM3H * 2.4 : flowM3H * 1.2;
  const pressureState = pressureStateFor(pressure, nominalPressure, Boolean(active));
  const assumptions: DemoAssumption[] = [
    block
      ? { key: 'demoAssumption.blockContext', params: { name: block.name } }
      : { key: 'demoAssumption.genericBlockContext' },
    olive
      ? { key: 'demoAssumption.oliveProfile' }
      : { key: 'demoAssumption.genericCropProfile' },
    {
      key: 'demoAssumption.irrigationType',
      params: { type: block?.irrigation_type || 'drip', flow: round(flowM3H, 2) },
    },
    {
      key: 'demoAssumption.linkedSensors',
      params: { count: linkedSensorCount },
    },
  ];

  return {
    source: 'demo',
    online: true,
    active,
    statusKey: 'demoTelemetry',
    lastUpdated: now.toISOString(),
    valveState: isValveType(item.type) ? (active ? 'open' : 'closed') : undefined,
    pumpState: isPumpType(item.type) || isSourceType(item.type) ? (active ? 'on' : 'off') : undefined,
    pressureState,
    waterDeliveryEstimate: waterDeliveryEstimate(flowM3H, Boolean(active)),
    values,
    fallbackKey: 'demoTelemetry',
    demoAssumptions: assumptions,
    history: {
      lastIrrigationEvent: lastIrrigationEvent(block, now),
      averagePressureBar: round(active ? pressure : nominalPressure * 0.2, 2),
      waterDeliveredTodayM3: round(waterToday, 2),
      soilMoistureTrend: active ? 'rising' : summer ? 'falling' : 'stable',
      lastUpdateTime: now.toISOString(),
    },
  };
};

const buildRealTelemetry = ({
  item,
  sensors,
  node,
  alerts,
}: {
  item: Equipment;
  sensors: Sensor[];
  node: IoTNode;
  alerts: IoTAlert[];
}): EquipmentTelemetry => {
  const measures = node.latest?.measures;
  const lastUpdated = node.latest?.ts || node.last_seen_at || undefined;
  const valve = getNodeValveState(node);
  const flow = getNodeFlow(node);
  const pressure = getPressure(measures);
  const soilMoisture = getSoilMoisture(measures);
  const soilEc = getSoilEc(measures);
  const soilTemp = getSoilTemperature(measures);
  const airTemp = getAirTemperature(measures);
  const airHumidity = getAirHumidity(measures);
  const current = getMeasure(measures, ['current_a']);
  const waterLevel = getMeasure(measures, ['water_level_m']);
  const battery = getNodeBattery(node);
  const pump = getPumpState(measures);
  const linkedSensorValues = sensors
    .map((sensor) => sensorValue(sensor, measures))
    .filter(Boolean) as TelemetryValue[];
  const values: TelemetryValue[] = [
    valve != null ? { key: 'valve_state', label: 'Valve', labelKey: 'valve', value: valve === 1 ? 'open' : 'closed' } : null,
    pump != null ? { key: 'pump_state', label: 'Pump', labelKey: 'pump', value: pump === 1 ? 'on' : 'off' } : null,
    flow != null ? { key: 'flow', label: 'Flow', labelKey: 'flow', value: `${flow} L/min` } : null,
    pressure != null ? { key: 'pressure', label: 'Pressure', labelKey: 'pressure', value: `${pressure} bar` } : null,
    soilMoisture != null ? { key: 'soil_moisture', label: 'Soil moisture', labelKey: 'soilMoisture', value: `${soilMoisture} %` } : null,
    soilEc != null ? { key: 'soil_ec', label: 'Soil EC', labelKey: 'soilEc', value: `${soilEc} µS/cm` } : null,
    soilTemp != null ? { key: 'soil_temperature', label: 'Soil temp', labelKey: 'soilTemperature', value: `${soilTemp} °C` } : null,
    airTemp != null ? { key: 'air_temperature', label: 'Air temp', labelKey: 'airTemperature', value: `${airTemp} °C` } : null,
    airHumidity != null ? { key: 'air_humidity', label: 'Air humidity', labelKey: 'airHumidity', value: `${airHumidity} %` } : null,
    current != null ? { key: 'current', label: 'Current', labelKey: 'current', value: `${current} A` } : null,
    waterLevel != null ? { key: 'water_level', label: 'Water level', labelKey: 'waterLevel', value: `${waterLevel} m` } : null,
    battery != null ? { key: 'battery', label: 'Battery', labelKey: 'battery', value: `${battery} V` } : null,
    ...linkedSensorValues,
  ].filter(Boolean) as TelemetryValue[];
  const alert = alertFor(item, node, alerts);
  const online = Boolean(lastUpdated);
  const valveState = valve == null ? undefined : valve === 1 ? 'open' : 'closed';
  const pumpState = pump == null ? undefined : pump === 1 ? 'on' : 'off';
  const active = valveState ? valveState === 'open' : pumpState ? pumpState === 'on' : null;

  return {
    source: 'real',
    online,
    active,
    statusKey: online ? 'online' : 'offline',
    sourceNodeId: node.id,
    sourceNodeName: node.name,
    lastUpdated,
    valveState,
    pumpState,
    alert: alert ? { severity: alert.severity, message: alert.message } : undefined,
    values,
    fallbackKey: 'noRecentData',
  };
};

export function buildEquipmentTelemetry({
  equipment,
  sensors,
  nodes,
  alerts,
  blocks = [],
  links = [],
  demo = { enabled: false },
}: {
  equipment: Equipment[];
  sensors: Sensor[];
  nodes: IoTNode[];
  alerts: IoTAlert[];
  blocks?: IrrigationBlock[];
  links?: HydraulicLink[];
  demo?: DemoContext;
}): Record<string, EquipmentTelemetry> {
  const sensorsByEquipment = new Map<string, Sensor[]>();
  sensors.forEach((sensor) => {
    if (!sensor.equipment_id) return;
    sensorsByEquipment.set(sensor.equipment_id, [...(sensorsByEquipment.get(sensor.equipment_id) || []), sensor]);
  });

  const now = demo.now || new Date();

  return Object.fromEntries(equipment.map((item) => {
    const node = findTelemetryNode(item, nodes);
    if (node) {
      return [item.id, buildRealTelemetry({
        item,
        sensors: sensorsByEquipment.get(item.id) || [],
        node,
        alerts,
      })];
    }

    if (demo.enabled) {
      return [item.id, buildDemoTelemetry({
        item,
        allEquipment: equipment,
        sensors,
        blocks,
        links,
        now,
      })];
    }

    return [item.id, {
      source: 'none' as const,
      online: false,
      active: null,
      statusKey: 'noTelemetrySource',
      values: [],
      fallbackKey: 'noTelemetrySource',
    }];
  }));
}
